import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Platform, Pressable, View } from 'react-native';

import { addressToString } from '../../../common/utils/address';
import { Text } from '../../components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import { MapLink } from '../map/MapLink';
import { buildMapHtml, hasCoordinates } from './driverRouteMapHtml';

let WebView = null;
if (Platform.OS !== 'web') {
	const { WebView: ReactNativeWebView } = require('react-native-webview');
	WebView = ReactNativeWebView;
}

const getLocationKey = location => location?.id || (hasCoordinates(location) ? location.coordinates.join(',') : null);

// "3-7" for consecutive stop numbers, "3, 5" otherwise.
const formatStopNumbers = (numbers) => {
	const ranges = [];
	numbers.forEach((number) => {
		const lastRange = ranges[ranges.length - 1];
		if (lastRange && number === lastRange[1] + 1) {
			lastRange[1] = number;
		}
		else {
			ranges.push([number, number]);
		}
	});

	return ranges.map(([first, last]) => (first === last ? `${first}` : `${first}-${last}`)).join(', ');
};

const getPinStatus = (stops) => {
	if (stops.every(stop => stop.status === 'complete')) {
		return 'complete';
	}

	return stops.some(stop => stop.status === 'current') ? 'current' : 'pending';
};

/**
 * Route map pins from route points (useDriverDayModel routeMapPoints): one
 * numbered pin per site, labelled with its stop numbers ("3-7"), plus one base
 * pin for the start and end locations. Each pin: { id, kind ('stop' | 'base'),
 * label, status ('complete' | 'current' | 'pending' | 'base'), name, subtitle,
 * coordinates }.
 */
export const buildRoutePins = (points) => {
	const pins = [];
	const pinsByKey = new Map();
	let stopNumber = 0;

	(points || []).forEach((point) => {
		const isStop = point.kind === 'stop';
		if (isStop) {
			stopNumber += 1;
		}

		const locationKey = getLocationKey(point.location);
		const key = locationKey ? `${isStop ? 'stop' : 'base'}:${locationKey}` : `point:${point.id}`;
		let pin = pinsByKey.get(key);
		if (!pin) {
			pin = {
				id: key,
				kind: isStop ? 'stop' : 'base',
				location: point.location,
				coordinates: hasCoordinates(point.location) ? point.location.coordinates : null,
				name: isStop ? (point.location?.name || point.name || point.label) : (point.name || point.label),
				stops: [],
				baseKinds: [],
			};
			pinsByKey.set(key, pin);
			pins.push(pin);
		}

		if (isStop) {
			pin.stops.push({ number: stopNumber, status: point.status });
		}
		else {
			pin.baseKinds.push(point.kind);
		}
	});

	return pins.map((pin) => {
		const address = addressToString(pin.location?.addressObj);
		if (pin.kind === 'base') {
			const hasStart = pin.baseKinds.includes('start');
			const hasEnd = pin.baseKinds.includes('end');
			const role = hasStart && hasEnd ? 'Start and end' : (hasStart ? 'Start' : 'End');
			return {
				id: pin.id,
				kind: 'base',
				label: hasStart && hasEnd ? 'S/E' : (hasStart ? 'S' : 'E'),
				status: 'base',
				name: pin.name,
				subtitle: [address, role].filter(Boolean).join(' · '),
				coordinates: pin.coordinates,
			};
		}

		const doneCount = pin.stops.filter(stop => stop.status === 'complete').length;
		return {
			id: pin.id,
			kind: 'stop',
			label: formatStopNumbers(pin.stops.map(stop => stop.number)),
			status: getPinStatus(pin.stops),
			name: pin.name,
			subtitle: [address, `${doneCount} of ${pin.stops.length} done`].filter(Boolean).join(' · '),
			coordinates: pin.coordinates,
		};
	});
};

// react-native-web has no WebView: the same map page runs in an iframe and
// talks to the app with postMessage.
const WebMapFrame = ({ html, frameRef, onMessage }) => {
	useEffect(() => {
		const handleMessage = (event) => {
			if (event.source === frameRef.current?.contentWindow) {
				onMessage(event.data);
			}
		};
		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, [frameRef, onMessage]);

	return React.createElement('iframe', {
		ref: frameRef,
		srcDoc: html,
		title: 'Route map',
		style: { border: 0, width: '100%', height: '100%', display: 'block' },
	});
};

/**
 * Route map of a run (Leaflet in a WebView, an iframe on web): numbered pins
 * per site and the road route between them, no names on the map (SPEC
 * decision 15).
 * Props:
 * - points: route points (useDriverDayModel routeMapPoints)
 * - mapHeight (default 260); style: container style (e.g. { flex: 1 } for a full-screen map)
 * - selectedPinId, onSelectPin(pinId | null): pin taps (buildRoutePins ids); the map
 *   highlights the selected pin
 * - onPress + interactive={false}: the whole map is one button (preview on the classic board)
 */
const DriverRouteMap = ({
	points,
	mapHeight = 260,
	style,
	onPress,
	interactive = true,
	selectedPinId = null,
	onSelectPin,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui } } = useTheme();
	const webViewRef = useRef(null);
	const frameRef = useRef(null);

	const pins = useMemo(() => buildRoutePins(points), [points]);
	const mappablePinCount = pins.filter(pin => pin.coordinates).length;
	const mapHtml = useMemo(() => buildMapHtml(points, pins, ui.colors, ui.isDark), [points, pins, ui.colors, ui.isDark]);
	const canShowMap = (Platform.OS === 'web' || WebView) && mappablePinCount > 0;

	const handleMessage = useCallback((data) => {
		if (!onSelectPin || typeof data !== 'string') {
			return;
		}

		try {
			const message = JSON.parse(data);
			if (message?.type === 'pin') {
				onSelectPin(message.id || null);
			}
		}
		catch (error) {
			// Not a map message.
		}
	}, [onSelectPin]);

	// Keep the map's highlight in step when the app clears or changes the selection.
	useEffect(() => {
		if (Platform.OS === 'web') {
			frameRef.current?.contentWindow?.selectPin?.(selectedPinId);
		}
		else {
			webViewRef.current?.injectJavaScript(`window.selectPin && window.selectPin(${JSON.stringify(selectedPinId)}); true;`);
		}
	}, [selectedPinId]);

	if (!points?.length) {
		return <Text style={styles.emptyText}>No route points are available for this service run.</Text>;
	}

	if (!canShowMap) {
		return (
			<View style={styles.fallbackContainer}>
				<Text style={styles.emptyText}>Map preview unavailable. Route points:</Text>
				{
					points.map(point => (
						<View key={point.id} style={styles.fallbackPoint}>
							<Text style={styles.pointLabel}>{point.label}</Text>
							{
								point.name && point.name !== point.label && (
									<Text style={styles.pointName}>{point.name}</Text>
								)
							}
							<MapLink location={point.location} />
						</View>
					))
				}
			</View>
		);
	}

	return (
		<View style={[styles.mapContainer, style || { height: mapHeight }]}>
			{Platform.OS === 'web'
				? <WebMapFrame html={mapHtml} frameRef={frameRef} onMessage={handleMessage} />
				: (
					<WebView
						ref={webViewRef}
						originWhitelist={['*']}
						source={{ html: mapHtml }}
						javaScriptEnabled={true}
						domStorageEnabled={true}
						scrollEnabled={false}
						onMessage={event => handleMessage(event.nativeEvent.data)}
					/>
				)}
			{
				onPress && !interactive && (
					<Pressable
						style={styles.mapPressOverlay}
						onPress={onPress}
						accessibilityRole="button"
						accessibilityLabel="Open the route map"
					>
						<View />
					</Pressable>
				)
			}
		</View>
	);
};

const createStyles = ({ ui: { colors, radius, space, type } }) => ({
	mapContainer: {
		borderRadius: radius.card,
		overflow: 'hidden',
		borderWidth: 1,
		borderColor: colors.border,
		backgroundColor: colors.card,
	},
	mapPressOverlay: {
		position: 'absolute',
		left: 0,
		right: 0,
		top: 0,
		bottom: 0,
	},
	fallbackContainer: {
		gap: space.sm,
	},
	fallbackPoint: {
		borderWidth: 1,
		borderColor: colors.border,
		borderRadius: radius.card,
		paddingVertical: space.sm,
		paddingHorizontal: space.md,
		backgroundColor: colors.card,
	},
	pointLabel: {
		...type.caption,
		color: colors.textSecondary,
	},
	pointName: {
		...type.bodySmallStrong,
		color: colors.text,
	},
	emptyText: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
});

export default DriverRouteMap;
